/**
 * Translation Service
 *
 * Handles all translation-related API operations
 * Manages CRUD operations for translations and provides
 * specialized methods for I18n system integration
 *
 * Domain: Translations
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TRANSLATION_ENDPOINTS } from '../../../api';
import { Observable, catchError, map } from 'rxjs';
import type {
  TranslationDictionary,
  TranslationQueryParams,
  TranslationRead,
  TranslationResponseDTO,
} from '../models/translation.types';

/**
 * Translation Service
 *
 * Provides methods for:
 * - CRUD operations on translations
 * - Fetching translations by language code
 * - I18n system integration
 * - Translation key-value mapping
 *
 * @example
 * ```typescript
 * // Inject service
 * private translationService = inject(TranslationService);
 *
 * // Get all translations
 * this.translationService.getAllTranslations({ language_id: 1 })
 *   .subscribe(translations => console.log(translations));
 *
 * // Get translations for I18n (key-value pairs)
 * this.translationService.getTranslations('de', 1001)
 *   .subscribe(dict => console.log(dict));
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  protected http = inject(HttpClient);

  /**
   * Create HTTP headers with authorization token
   */
  private createHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    });
  }

  /**
   * Build URL with query parameters
   */
  private buildUrlWithParams(
    baseUrl: string,
    params: Record<string, string | number>
  ): string {
    const url = new URL(baseUrl, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
    return url.toString();
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('Translation Service Error:', error);
    throw error;
  }

  /**
   * Get all translations with optional filtering
   *
   * Supports pagination and filtering by language and category
   *
   * @param query - Query parameters for filtering and pagination
   * @returns Observable of translation array
   *
   * @example
   * ```typescript
   * // Get all translations for German (language_id = 2)
   * this.translationService.getAllTranslations({
   *   language_id: 2,
   *   category_id: 1001,
   *   skip: 0,
   *   limit: 100
   * }).subscribe(translations => {
   *   console.log('Translations:', translations);
   * });
   * ```
   */
  getAllTranslations(
    query?: TranslationQueryParams
  ): Observable<TranslationRead[]> {
    const params: Record<string, string | number> = {};

    if (query) {
      if (query.skip !== undefined) params['skip'] = query.skip;
      if (query.limit !== undefined) params['limit'] = query.limit;
      if (query.language_id) params['language_id'] = query.language_id;
      if (query.category_id) params['category_id'] = query.category_id;
    }

    return this.http
      .get<TranslationRead[]>(
        this.buildUrlWithParams(TRANSLATION_ENDPOINTS.list, params),
        { headers: this.createHeaders() }
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Get translations by language code (HAUPTMETHODE für I18n Store)
   *
   * Returns translations in a grouped format with language metadata
   * This is the primary method used by the I18n translation system
   *
   * @param languageCode - ISO language code (e.g., 'de', 'en', 'fr')
   * @param categoryId - Optional category filter (default: 1001)
   * @returns Observable of translation response with language metadata
   *
   * @example
   * ```typescript
   * // Get German translations for category 1001
   * this.translationService.getTranslationsByLanguage('de', 1001)
   *   .subscribe(response => {
   *     console.log('Language:', response.language_name);
   *     console.log('Translations:', response.translations);
   *   });
   * ```
   */
  getTranslationsByLanguage(
    languageCode: string,
    categoryId: number = 1001
  ): Observable<TranslationResponseDTO> {
    const params: Record<string, number> = {
      category_id: categoryId,
      skip: 0,
      limit: 10000, // Get all translations for language
    };

    // Endpoint: /translations/by-language/{code}
    const url = `${TRANSLATION_ENDPOINTS.list}by-language/${languageCode}`;

    return this.http
      .get<TranslationResponseDTO>(this.buildUrlWithParams(url, params), {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Get translations as key-value dictionary
   *
   * Convenient method that returns translations as a simple
   * Record<string, string> for direct use in components
   *
   * @param languageCode - ISO language code
   * @param categoryId - Category ID (default: 1001)
   * @returns Observable of translation dictionary
   *
   * @example
   * ```typescript
   * // Get translations as dictionary
   * this.translationService.getTranslations('de', 1001)
   *   .subscribe(dict => {
   *     console.log(dict['welcome']); // "Willkommen"
   *     console.log(dict['goodbye']); // "Auf Wiedersehen"
   *   });
   * ```
   */
  getTranslations(
    languageCode: string,
    categoryId: number = 1001
  ): Observable<TranslationDictionary> {
    return this.getTranslationsByLanguage(languageCode, categoryId).pipe(
      map((response: TranslationResponseDTO) => {
        const translationMap: TranslationDictionary = {};
        response.translations.forEach((t) => {
          translationMap[t.key] = t.value;
        });
        return translationMap;
      }),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get specific translation by key
   *
   * Fetches a single translation by its key, language, and category
   *
   * @param languageCode - ISO language code
   * @param categoryId - Category ID
   * @param translationKey - Translation key
   * @returns Observable of translation key-value pair
   *
   * @example
   * ```typescript
   * this.translationService.getTranslationByKey('de', 1001, 'welcome')
   *   .subscribe(translation => {
   *     console.log(`${translation.key}: ${translation.value}`);
   *     // Output: "welcome: Willkommen"
   *   });
   * ```
   */
  getTranslationByKey(
    languageCode: string,
    categoryId: number,
    translationKey: string
  ): Observable<{ key: string; value: string }> {
    return this.getTranslations(languageCode, categoryId).pipe(
      map((dict) => ({
        key: translationKey,
        value: dict[translationKey] || translationKey,
      })),
      catchError(this.handleError.bind(this))
    );
  }
}
